import { App, Button, Empty, List, Popconfirm, Progress, Skeleton, Space, Tag, Typography } from 'antd';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { NocSimScenario } from '@goelectric/shared-types';
import type { ScenarioRunView } from '../hooks/useSimulation';

const STATUS_TAG: Record<ScenarioRunView['status'], { color: string; label: string }> = {
  RUNNING: { color: 'processing', label: 'En ejecución' },
  COMPLETED: { color: 'success', label: 'Completado' },
  FAILED: { color: 'error', label: 'Fallido' },
};

interface ScenarioPanelProps {
  catalog: NocSimScenario[];
  loading: boolean;
  error: string | null;
  runs: ScenarioRunView[];
  startingId: string | null;
  onStart: (scenarioId: string) => Promise<void>;
  onReload: () => void;
}

/** Catálogo de escenarios de un clic + progreso en vivo (SSE). */
export function ScenarioPanel({
  catalog,
  loading,
  error,
  runs,
  startingId,
  onStart,
  onReload,
}: ScenarioPanelProps) {
  const { message } = App.useApp();

  const start = async (scenarioId: string) => {
    try {
      await onStart(scenarioId);
      message.success(`Escenario «${scenarioId}» iniciado. Sigue el progreso abajo.`);
    } catch {
      message.error('No se pudo iniciar el escenario.');
    }
  };

  if (loading && catalog.length === 0) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }
  if (error) {
    return (
      <Space direction="vertical">
        <Typography.Text type="danger">{error}</Typography.Text>
        <Button size="small" icon={<ReloadOutlined />} onClick={onReload}>
          Reintentar
        </Button>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <List
        size="small"
        dataSource={catalog}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin escenarios" /> }}
        renderItem={(scenario) => (
          <List.Item
            actions={[
              <Popconfirm
                key="run"
                title={`¿Ejecutar «${scenario.name}»?`}
                description="El escenario muta el estado de la red de simulación."
                okText="Ejecutar"
                cancelText="Cancelar"
                onConfirm={() => start(scenario.id)}
              >
                <Button
                  type="primary"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  loading={startingId === scenario.id}
                >
                  Ejecutar
                </Button>
              </Popconfirm>,
            ]}
          >
            <List.Item.Meta
              title={scenario.name}
              description={
                <Space direction="vertical" size={2}>
                  <Typography.Text type="secondary">{scenario.description}</Typography.Text>
                  <Space wrap size={4}>
                    {scenario.stationIds.map((stationId) => (
                      <Tag key={stationId}>{stationId}</Tag>
                    ))}
                    <Tag color="default">{scenario.steps.length} pasos</Tag>
                  </Space>
                </Space>
              }
            />
          </List.Item>
        )}
      />

      {runs.length > 0 && (
        <>
          <Typography.Text strong>Ejecuciones recientes</Typography.Text>
          {runs.map((run) => {
            const tag = STATUS_TAG[run.status];
            return (
              <div key={run.runId} className="lab-run">
                <Space wrap size="small" style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space size="small">
                    <Tag color={tag.color}>{tag.label}</Tag>
                    <Typography.Text strong>{run.scenarioId}</Typography.Text>
                    <Typography.Text type="secondary">
                      paso {run.step}/{run.totalSteps}
                    </Typography.Text>
                  </Space>
                  <Typography.Text type="secondary" style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 11 }}>
                    {run.runId.slice(0, 8)}
                  </Typography.Text>
                </Space>
                <Progress
                  percent={run.percent}
                  size="small"
                  status={
                    run.status === 'FAILED'
                      ? 'exception'
                      : run.status === 'COMPLETED'
                        ? 'success'
                        : 'active'
                  }
                />
                {run.label && <Typography.Text type="secondary">{run.label}</Typography.Text>}
                {run.error && <Typography.Text type="danger">{run.error}</Typography.Text>}
                {run.logs.length > 0 && (
                  <pre className="lab-log">{run.logs.slice(-6).join('\n')}</pre>
                )}
              </div>
            );
          })}
        </>
      )}
    </Space>
  );
}
